import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	ElementRef,
	Injector,
	NgZone,
	OnDestroy,
	Renderer2,
	SecurityContext,
	afterNextRender,
	computed,
	effect,
	inject,
	input,
	signal,
	untracked,
	viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { _IdGenerator } from '@angular/cdk/a11y';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NgControl, ValidationErrors, Validator } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { RichTextCodec, RichTextSymbol, RichTextTool, RichTextToolDefinition, RichTextToolbarItem } from './rich-text-tool.type';
import { DEFAULT_RICH_TEXT_TOOLS, RICH_TEXT_SYMBOLS, RICH_TEXT_TOOL_DEFINITIONS } from './rich-text-tools';
import {
	alignmentToClasses,
	escapeHtml,
	isSurfaceBlank,
	isSurfaceEmpty,
	normalizeLinkUrl,
	stripPasteWrappers,
	visibleTextLength
} from './rich-text-html.functions';

const sameCommands = (a: ReadonlySet<string>, b: ReadonlySet<string>): boolean => a.size === b.size && [...a].every((command) => b.has(command));

/**
 * The suite's textarea. Plain text by default; `isRich` swaps the `<textarea>` for a formatting
 * toolbar and an editable surface, and the form value becomes an HTML string. Switching a field to
 * rich text changes what gets saved, so only do it for text that is shown nowhere but our own UI.
 *
 * Rich mode is built on the browser's own editing (`contentEditable` + `execCommand`) so it adds no
 * dependency; if the suite outgrows that (tables, images, collaborative editing), swap the internals
 * here for a maintained editor and every caller follows. The rich value is written back only from a
 * real edit. Seeding never emits, so a record that is opened and saved without typing keeps its
 * stored value byte-for-byte, even though an `innerHTML` round trip would otherwise rewrite entities.
 */
const VOID_ELEMENTS = new Set(['BR', 'HR', 'IMG', 'INPUT', 'WBR']);

@Component({
	selector: 'nha-textarea',
	templateUrl: './nha-textarea.component.html',
	styleUrls: ['./nha-textarea.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, MatTooltipModule, CdkTextareaAutosize],
	providers: [
		// The visible-length validator rides NG_VALIDATORS so Angular merges, re-attaches on a control
		// swap and cleans it up exactly like a `required` attribute. It resolves the component lazily:
		// a `useExisting` provider would cycle with the NgControl this component injects.
		{
			provide: NG_VALIDATORS,
			multi: true,
			deps: [Injector],
			useFactory: (injector: Injector): Validator => {
				let field: NHATextareaComponent | null = null;
				const host = () => (field ??= injector.get(NHATextareaComponent));
				return {
					validate: (control) => host().validateRichLength(control),
					registerOnValidatorChange: (fn) => host().registerOnValidatorChange(fn)
				};
			}
		}
	],
	host: {
		'[class.nha-textarea-outline]': "appearance() === 'outline'",
		'[class.nha-textarea-rich]': 'isRich()',
		'[style.--nha-textarea-rows]': 'rows()'
	}
})
export class NHATextareaComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
	readonly ngControl = inject(NgControl, { self: true, optional: true });
	private cdr = inject(ChangeDetectorRef);
	private destroyRef = inject(DestroyRef);
	private injector = inject(Injector);
	private renderer = inject(Renderer2);
	private zone = inject(NgZone);
	private sanitizer = inject(DomSanitizer);

	private matInput = viewChild(MatInput);
	private readonly surface = viewChild<ElementRef<HTMLDivElement>>('surface');
	private readonly toolbar = viewChild<ElementRef<HTMLDivElement>>('toolbar');
	private readonly linkTextInput = viewChild<ElementRef<HTMLInputElement>>('linkTextInput');
	private readonly linkInput = viewChild<ElementRef<HTMLInputElement>>('linkInput');

	// The formControlName lives on this wrapper, so the inner matInput has no NgControl of its own —
	// which leaves its errorState permanently false, and MatFormField only projects a <mat-error>
	// when the control's errorState is true. Feed the inner matInput our host control's validity via
	// this matcher (see pumpErrorState() for when it is re-evaluated) so the message actually renders.
	readonly errorStateMatcher: ErrorStateMatcher = {
		isErrorState: () => this.hasError()
	};

	readonly label = input.required<string>();
	/**
	 * Renders the field without its visible label, for callers that already show the caption
	 * themselves. Opt-in: the default keeps the label exactly as before.
	 *
	 * The label element is omitted entirely rather than hidden with CSS, because its mere presence
	 * changes Material's behaviour — it reserves the floating-label row, notches the outline around
	 * nothing, and paints a select's placeholder transparent (`mat-form-field-hide-placeholder` is
	 * `_hasFloatingLabel() && !_shouldLabelFloat()`). `label` stays required and becomes the control's
	 * `aria-label`, so the accessible name survives.
	 */
	readonly hideLabel = input<boolean>(false);
	/** In rich mode this counts the characters a reader sees, not the markup. */
	readonly maxLength = input<number | null>(null);
	/**
	 * 'fill' (default) keeps the NHA white-card look with the label inside. 'outline' opts into
	 * Material's notched-outline look (label on the top border), mirroring nha-text-input / nha-select.
	 */
	readonly appearance = input<'fill' | 'outline'>('fill');
	// Opt-in (default off, existing usages unaffected). Needed for an empty label (label="") to let
	// the placeholder show while unfocused — Material only reveals it once the label has floated
	// out of the value area.
	readonly floatLabelAlways = input<boolean>(false);
	readonly hint = input<string>('');
	readonly rows = input<number>(3);
	/** Plain mode only: grow with the content between minRows and maxRows; `rows` is ignored while on. */
	readonly autoGrow = input<boolean>(false);
	readonly minRows = input<number | null>(null);
	readonly maxRows = input<number | null>(null);
	readonly placeholder = input<string>('');
	readonly errors = input<Record<string, string>>({});
	readonly required = input<boolean>(false);
	/** Opt-in formatting toolbar; the form value becomes an HTML string. */
	readonly isRich = input<boolean>(false);
	readonly tools = input<RichTextTool[]>(DEFAULT_RICH_TEXT_TOOLS);
	readonly codec = input<RichTextCodec | null>(null);

	readonly value = signal<string | null>(null);
	readonly disabled = signal<boolean>(false);
	readonly isBlank = signal<boolean>(true);
	readonly activeCommands = signal<ReadonlySet<string>>(new Set(), { equal: sameCommands });
	readonly toolbarFocusIndex = signal<number>(0);

	/** The toolbar's inline panel, if any. One at a time. */
	readonly openPanel = signal<'link' | 'symbol' | null>(null);
	readonly symbols = RICH_TEXT_SYMBOLS;
	readonly linkText = signal<string>('');
	readonly linkDraft = signal<string>('');
	readonly linkError = signal<string | null>(null);
	readonly editedAnchor = signal<HTMLAnchorElement | null>(null);

	readonly toolbarItems = computed<RichTextToolbarItem[]>(() => {
		let buttonIndex = 0;
		return this.tools().map((tool) => (tool === 'separator' ? { id: 'separator' } : { ...RICH_TEXT_TOOL_DEFINITIONS[tool], buttonIndex: buttonIndex++ }));
	});

	readonly labelId = inject(_IdGenerator).getId('nha-textarea-label-');
	readonly subscriptId = inject(_IdGenerator).getId('nha-textarea-subscript-');

	/** What the surface currently shows, so seeding skips what is already on screen (and never fights the caret). */
	private surfaceValue: string | null = null;
	private seededSurface: HTMLElement | null = null;
	private seededCodec: RichTextCodec | null = null;
	private savedRange: Range | null = null;
	private stopSelectionListener: (() => void) | null = null;
	private toolbarRefreshFrame: number | null = null;

	private onChange: (value: string | null) => void = () => {};
	private onTouched: () => void = () => {};
	private onValidatorChange: () => void = () => {};

	constructor() {
		if (this.ngControl) {
			this.ngControl.valueAccessor = this;
		}

		// Also reads the viewChild, so it re-runs once the rich surface mounts.
		effect(() => {
			if (!this.isRich()) {
				return;
			}
			const value = this.value();
			const codec = this.codec();
			const el = this.surface()?.nativeElement;

			if (!el || (el === this.seededSurface && codec === this.seededCodec && (value ?? '') === (this.surfaceValue ?? ''))) {
				return;
			}

			el.innerHTML = this.encode(value ?? '');
			alignmentToClasses(el);
			this.seededSurface = el;
			this.seededCodec = codec;
			// Remembered as the surface serializes it, so a command that changes nothing reads back as no change.
			this.surfaceValue = isSurfaceEmpty(el) ? null : this.decode(el.innerHTML);
			this.isBlank.set(isSurfaceBlank(el));
		});

		let initialised = false;
		effect(() => {
			this.maxLength();
			this.isRich();
			if (initialised) {
				// Re-validating inside the effect would change form state mid change detection.
				untracked(() => queueMicrotask(() => this.onValidatorChange()));
			}
			initialised = true;
		});
	}

	ngAfterViewInit(): void {
		// events() fires on value, status AND touched changes — the last is what markAllAsTouched()
		// (form submit) triggers without emitting statusChanges, so this covers both the blur path and
		// the "hit Submit without leaving the field" path.
		this.ngControl?.control?.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.pumpErrorState());
	}

	ngOnDestroy(): void {
		this.stopSelectionListener?.();
		if (this.toolbarRefreshFrame !== null) {
			cancelAnimationFrame(this.toolbarRefreshFrame);
		}
	}

	writeValue(value: string | null): void {
		this.value.set(value ?? null);
	}

	registerOnChange(fn: (value: string | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	registerOnValidatorChange(fn: () => void): void {
		this.onValidatorChange = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled.set(isDisabled);
		if (isDisabled) {
			this.closePanels();
		}
	}

	// A contentEditable surface has no native maxlength, so rich mode validates the visible text instead.
	validateRichLength(control: AbstractControl): ValidationErrors | null {
		const max = this.maxLength();
		if (!this.isRich() || max === null) {
			return null;
		}
		const actualLength = visibleTextLength(control.value);
		return actualLength > max ? { maxlength: { requiredLength: max, actualLength } } : null;
	}

	onInput(raw: string): void {
		const next = raw === '' ? null : raw;
		this.value.set(next);
		this.onChange(next);
	}

	onBlur(): void {
		this.onTouched();
	}

	// ── Rich mode ──────────────────────────────────────────────────────────────

	onRichInput(): void {
		this.pull();
	}

	/**
	 * Pasted HTML is stored as readers will see it: sanitized the way `[innerHTML]` sanitizes on
	 * display, so the editor never shows styling that disappears once the note is saved.
	 */
	onPaste(event: ClipboardEvent): void {
		const data = event.clipboardData;
		if (!data || this.disabled()) {
			return;
		}
		// Images would be stored as multi-megabyte data URIs inside the note text.
		if ((data.files?.length ?? 0) > 0 && !data.getData('text/html')) {
			event.preventDefault();
			return;
		}
		const html = data.getData('text/html');
		if (!html) {
			return;
		}
		event.preventDefault();
		const clean = (this.sanitizer.sanitize(SecurityContext.HTML, stripPasteWrappers(html)) ?? '').replace(/<img\b[^>]*>/gi, '');
		if (clean.trim()) {
			this.exec('insertHTML', clean);
			return;
		}
		// Nothing survived sanitizing (an image with a stray wrapper, say): fall back to the plain text
		// rather than inserting nothing over the selection.
		const text = data.getData('text/plain');
		if (text) {
			this.exec('insertText', text);
		}
	}

	// The selection listener exists only while the editor has focus, so the ~35 plain-mode fields
	// across the suite never hear about document selection changes at all. It runs outside the zone
	// and once per frame: a drag-selection fires selectionchange on every mouse move.
	onFocusIn(): void {
		this.stopSelectionListener ??= this.zone.runOutsideAngular(() =>
			this.renderer.listen('document', 'selectionchange', () => {
				this.toolbarRefreshFrame ??= requestAnimationFrame(() => {
					this.toolbarRefreshFrame = null;
					this.refreshToolbarState();
				});
			})
		);
		this.refreshToolbarState();
	}

	onFocusOut(event: FocusEvent): void {
		const frame = event.currentTarget as HTMLElement;
		if (event.relatedTarget instanceof Node && frame.contains(event.relatedTarget)) {
			return;
		}
		this.stopSelectionListener?.();
		this.stopSelectionListener = null;
		this.activeCommands.set(new Set());
		this.onTouched();
	}

	run(tool: RichTextToolDefinition): void {
		if (tool.id === 'link') {
			this.openLinkPopover();
			return;
		}
		if (tool.id === 'symbol') {
			this.toggleSymbolPanel();
			return;
		}
		this.exec(tool.command, tool.commandValue);
	}

	isActive(tool: RichTextToolDefinition): boolean {
		return this.activeCommands().has(tool.command);
	}

	/** Toolbar pattern: one tab stop, arrow keys move between the buttons. */
	onToolbarKeydown(event: KeyboardEvent): void {
		// Focus stays on the toolbar after opening a panel; Escape there closes the panel, not the field's host.
		if (event.key === 'Escape' && this.openPanel()) {
			event.preventDefault();
			event.stopPropagation();
			this.cancelPanel();
			return;
		}
		const buttons = this.toolbarButtons();
		if (!buttons.length) {
			return;
		}
		const current = Math.max(0, buttons.indexOf(document.activeElement as HTMLButtonElement));
		const last = buttons.length - 1;
		let next: number;
		switch (event.key) {
			case 'ArrowRight':
			case 'ArrowDown':
				next = current === last ? 0 : current + 1;
				break;
			case 'ArrowLeft':
			case 'ArrowUp':
				next = current === 0 ? last : current - 1;
				break;
			case 'Home':
				next = 0;
				break;
			case 'End':
				next = last;
				break;
			default:
				return;
		}
		event.preventDefault();
		this.toolbarFocusIndex.set(next);
		buttons[next].focus();
	}

	openLinkPopover(): void {
		if (this.disabled()) {
			return;
		}
		if (this.openPanel() === 'link') {
			this.cancelPanel();
			return;
		}
		const el = this.surface()?.nativeElement;
		const range = this.selectionInSurface();
		this.savedRange = range;

		this.editedAnchor.set(range && el ? this.linkUnderSelection(el, range) : null);

		const selectedText = this.editedAnchor()?.textContent ?? (range ? this.textOf(range) : '');
		this.linkText.set(selectedText);
		this.linkDraft.set(this.editedAnchor()?.getAttribute('href') ?? '');
		this.linkError.set(null);
		this.openPanel.set('link');
		afterNextRender(() => (selectedText ? this.linkInput() : this.linkTextInput())?.nativeElement.select(), { injector: this.injector });
	}

	onLinkTextInput(value: string): void {
		this.linkText.set(value);
	}

	onLinkDraftInput(value: string): void {
		this.linkDraft.set(value);
		this.linkError.set(null);
	}

	/**
	 * Replaces the selection (or the link being edited) with one anchor. A single insert rather than
	 * the browser's createLink, whose handling of existing links and empty selections varies by browser.
	 */
	applyLink(): void {
		const prefixes = this.codec()?.linkPrefixes ?? [];
		const url = normalizeLinkUrl(this.linkDraft(), prefixes);
		if (!url) {
			this.linkError.set(`Enter an https:// or mailto: link${prefixes.length ? `, or start with ${prefixes.join(' or ')}` : ''}.`);
			return;
		}

		// The surface stays editable while the popover is open, so the link may have been deleted meanwhile.
		const anchor = this.editedAnchor()?.isConnected ? this.editedAnchor() : null;
		const range = this.liveSavedRange();
		const text = this.linkText().trim();
		this.closePanels();

		this.restoreSelection(anchor ? this.rangeOf(anchor) : range);
		const source = anchor ?? range;
		const sourceText = anchor ? (anchor.textContent ?? '') : range ? this.textOf(range) : '';
		const typedTheSame = source !== null && text !== '' && text === sourceText.trim();
		// Keep inline formatting the author already had when the text was not retyped.
		const inner = typedTheSame ? this.inlineHtmlOf(source) : escapeHtml(text || url);
		// Links open in a new tab so a reader does not lose the page the note lives on.
		this.exec('insertHTML', `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${inner}</a>`);
		this.pruneEmptyInline();
	}

	/** Saves the caret first: picking a symbol moves focus off the surface. */
	toggleSymbolPanel(): void {
		if (this.disabled()) {
			return;
		}
		if (this.openPanel() === 'symbol') {
			this.cancelPanel();
			return;
		}
		// From the link panel, focus is in its inputs; keep the caret it saved.
		if (this.openPanel() === null) {
			this.savedRange = this.selectionInSurface();
		}
		this.closePanels();
		this.openPanel.set('symbol');
	}

	insertSymbol(symbol: RichTextSymbol): void {
		// The symbol buttons don't take focus, so the author's current caret wins.
		const range = this.selectionInSurface() ?? this.liveSavedRange();
		this.closePanels();
		this.restoreSelection(range);
		this.exec('insertText', symbol.char);
	}

	removeLink(): void {
		const anchor = this.editedAnchor();
		this.closePanels();
		if (!anchor?.isConnected) {
			return;
		}
		this.restoreSelection(this.rangeAround(anchor));
		this.exec('unlink');
	}

	closePanels(): void {
		this.openPanel.set(null);
		this.linkError.set(null);
	}

	/** Closes the open panel and restores the caret. */
	cancelPanel(): void {
		const range = this.liveSavedRange();
		this.closePanels();
		this.restoreSelection(range);
	}

	/** The surface stays editable while a panel is open, so the saved caret may be gone. */
	private liveSavedRange(): Range | null {
		return this.savedRange && this.isLive(this.savedRange) ? this.savedRange : null;
	}

	// ── Validation display ─────────────────────────────────────────────────────

	hasError(): boolean {
		// Key the field's error styling off whether a message will actually render, so we never show the
		// red state with no accompanying <mat-error>.
		return this.errorMessage !== null;
	}

	// Re-evaluate the inner matInput's errorState (via errorStateMatcher) and notify MatFormField.
	// updateErrorState() emits the control's stateChanges, which is what makes the (OnPush) form-field
	// re-render its subscript and show/hide the projected <mat-error>.
	private pumpErrorState(): void {
		this.matInput()?.updateErrorState();
		this.cdr.markForCheck();
	}

	get errorMessage(): string | null {
		// Error display is opt-in: a consumer signals it wants validation surfaced by passing an
		// [errors] map. Without it this shared control stays silent (many existing forms never wired
		// errors up and relied on it being quiet). This also gates errorState via hasError().
		const custom = this.errors();
		if (Object.keys(custom).length === 0) {
			return null;
		}
		const control = this.ngControl?.control;
		if (!control?.errors || !control.touched) {
			return null;
		}
		for (const key of Object.keys(control.errors)) {
			if (custom[key]) {
				return custom[key];
			}
		}
		if (control.errors['required']) {
			return `${this.label()} is required`;
		}
		if (control.errors['minlength']) {
			const req = control.errors['minlength'].requiredLength;
			return `Minimum ${req} characters`;
		}
		if (control.errors['maxlength']) {
			const req = control.errors['maxlength'].requiredLength;
			return `Maximum ${req} characters`;
		}
		return null;
	}

	// ── Rich mode internals ────────────────────────────────────────────────────

	private selectionInSurface(): Range | null {
		const el = this.surface()?.nativeElement;
		const selection = document.getSelection();
		const current = selection?.rangeCount ? selection.getRangeAt(0) : null;
		return current && el && el.contains(current.startContainer) && el.contains(current.endContainer) ? current.cloneRange() : null;
	}

	private exec(command: string, value?: string): void {
		if (this.disabled()) {
			return;
		}

		const el = this.surface()?.nativeElement;
		el?.focus();

		// `execCommand` is deprecated, and absent outside a real browser (jsdom has no implementation).
		// Bail rather than throw so the field still renders.
		if (typeof document.execCommand !== 'function') {
			return;
		}

		// Prefer semantic tags (<b>/<i>) over inline styles where the browser honors it.
		document.execCommand('styleWithCSS', false, 'false');
		document.execCommand(command, false, value);
		this.pull();
		// A toolbar command on a collapsed caret changes state without moving the selection.
		this.refreshToolbarState();
	}

	private pull(): void {
		const el = this.surface()?.nativeElement;
		if (!el) {
			return;
		}

		alignmentToClasses(el);
		const next = isSurfaceEmpty(el) ? null : this.decode(el.innerHTML);

		this.isBlank.set(isSurfaceBlank(el));
		if ((next ?? '') === (this.surfaceValue ?? '')) {
			return;
		}
		this.surfaceValue = next;
		this.value.set(next);
		this.onChange(next);
	}

	private refreshToolbarState(): void {
		if (typeof document.queryCommandState !== 'function') {
			return;
		}
		const el = this.surface()?.nativeElement;
		const selection = document.getSelection();
		if (!el || !selection?.anchorNode || !el.contains(selection.anchorNode)) {
			this.activeCommands.set(new Set());
			return;
		}

		const active = new Set<string>();
		for (const item of this.toolbarItems()) {
			if (item.id !== 'separator' && item.toggle && document.queryCommandState(item.command)) {
				active.add(item.command);
			}
		}
		this.activeCommands.set(active);
	}

	private restoreSelection(range: Range | null): void {
		const el = this.surface()?.nativeElement;
		if (!el) {
			return;
		}
		el.focus();
		const selection = document.getSelection();
		if (!selection) {
			return;
		}
		const target = range ?? this.rangeAtEnd(el);
		selection.removeAllRanges();
		selection.addRange(target);
	}

	private rangeAround(node: Node): Range {
		const range = document.createRange();
		range.selectNodeContents(node);
		return range;
	}

	private rangeOf(node: Node): Range {
		const range = document.createRange();
		range.selectNode(node);
		return range;
	}

	/**
	 * The link the selection is editing: the one enclosing the whole selection, or one the selection
	 * covers exactly (browsers often put the boundaries of a drag-selected link just outside it).
	 */
	private linkUnderSelection(el: HTMLElement, range: Range): HTMLAnchorElement | null {
		const startElement = range.startContainer instanceof Element ? range.startContainer : range.startContainer.parentElement;
		const enclosing = startElement?.closest('a');
		if (enclosing && el.contains(enclosing) && enclosing.contains(range.endContainer)) {
			return enclosing;
		}
		const selected = range.toString();
		return Array.from(el.querySelectorAll('a')).find((a) => range.intersectsNode(a) && a.textContent === selected && selected !== '') ?? null;
	}

	private isLive(range: Range): boolean {
		return range.startContainer.isConnected && range.endContainer.isConnected;
	}

	/** The selection's visible text, with soft line breaks read as spaces rather than run together. */
	private textOf(range: Range): string {
		const holder = document.createElement('div');
		holder.append(range.cloneContents());
		holder.querySelectorAll('br').forEach((br) => br.replaceWith(' '));
		return holder.textContent ?? '';
	}

	/** Replacing part of a selection leaves the split-off halves behind as empty tags; drop them. */
	private pruneEmptyInline(): void {
		const el = this.surface()?.nativeElement;
		// Split-off halves keep an empty text node, so :empty would miss them.
		const empties = Array.from(el?.querySelectorAll('a, b, i, u, strike, em, strong, span') ?? []).filter(
			(node) => node.textContent === '' && !node.querySelector('img, br')
		);
		if (!empties.length) {
			return;
		}
		empties.forEach((node) => node.remove());
		this.pull();
	}

	/** The selection's own markup when it is purely inline; a selection spanning blocks falls back to its text. */
	private inlineHtmlOf(source: HTMLAnchorElement | Range): string {
		const fragment = source instanceof Range ? source.cloneContents() : (source.cloneNode(true) as HTMLElement);
		const holder = document.createElement('div');
		holder.append(...Array.from(source instanceof Range ? fragment.childNodes : (fragment as HTMLElement).childNodes));
		holder.querySelectorAll('a').forEach((a) => a.replaceWith(...Array.from(a.childNodes)));
		const crossesBlocks = holder.querySelector('p, div, li, ol, ul, h1, h2, h3, h4, h5, h6, blockquote, pre, table, tr, td, th');
		return crossesBlocks ? escapeHtml(holder.textContent) : holder.innerHTML;
	}

	/** End of the last block's text, so content added without a caret lands inside the last paragraph. */
	private rangeAtEnd(el: HTMLElement): Range {
		let target: Node = el;
		while (target.lastChild instanceof HTMLElement && !VOID_ELEMENTS.has(target.lastChild.tagName)) {
			target = target.lastChild;
		}
		const range = document.createRange();
		range.selectNodeContents(target);
		range.collapse(false);
		return range;
	}

	private toolbarButtons(): HTMLButtonElement[] {
		return Array.from(this.toolbar()?.nativeElement.querySelectorAll<HTMLButtonElement>('button') ?? []);
	}

	private encode(value: string): string {
		return this.codec()?.encode(value) ?? value;
	}

	private decode(html: string): string {
		return this.codec()?.decode(html) ?? html;
	}
}
